import get from 'lodash/get';
import { googleSdkConfig as config, googleReCaptchaConfig, remarketing, language, analytics } from '../constants';

const googleSdkLink = `${config.libUrl}&v=${config.version}&key=${config.apiKey}&language=`;
const googleTagLink = `https://www.googletagmanager.com/gtag/js?id=${analytics.google.trackingId}`;
const turnstileLink = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export const scriptLoader = url => new Promise((resolve, reject) => {
  if (!global.document) {
    reject({ code: 'SSR', message: 'Server side script load attempt failed, no DOM constructed yet.' });
  } else {
    const anchorNode = global.document.getElementsByTagName('script')[0];
    const normalizer = global.document.createElement('a');
    normalizer.href = url;
    const normalizedUrl = normalizer.href;
    const existingScript = Array.from(global.document.getElementsByTagName('script'))
      .find(script => script.src === normalizedUrl);

    const handleLoad = (event, script) => {
      if (script) {
        script.setAttribute('data-loaded', 'true');
      }
      resolve(event);
    };

    const handleError = (err) => {
      reject(err);
    };

    if (existingScript) {
      if (existingScript.getAttribute('data-loaded') === 'true') {
        resolve();
      } else {
        existingScript.addEventListener('load', event => handleLoad(event, existingScript), { once: true });
        existingScript.addEventListener('error', handleError, { once: true });
      }
    } else {
      const script = global.document.createElement('script');

      script.type = 'text/javascript';
      script.src = url;
      script.defer = true;

      script.addEventListener('load', event => handleLoad(event, script), { once: true });
      script.addEventListener('error', handleError, { once: true });

      anchorNode.parentNode.insertBefore(script, anchorNode);
    }
  }
});

const persistentScriptLoader = (link, object, time = 5, requiredPath = '') => {
  const objectAlreadyExists = global[object] || window[object];
  const requiredPathAlreadyExist = get(global, requiredPath) || get(window, requiredPath);

  return (!objectAlreadyExists || (requiredPath && !requiredPathAlreadyExist)
      ? scriptLoader(link)
        .then(() => new Promise((resolve, reject) => {
          const objectLoaded = global[object] || window[object];
          const requiredPathLoaded = get(global, requiredPath) || get(window, requiredPath);

          if (!objectLoaded || (requiredPath && !requiredPathLoaded)) {
            let attempts = 0;
            // TODO: Possible issue with slow connections, may be switch to XHR + eval() approach
            const interval = setInterval(() => {
              const objectInited = global[object] || window[object];
              const requiredPathInited = get(global, requiredPath) || get(window, requiredPath);

              if (objectInited && (requiredPath ? requiredPathInited : true)) {
                resolve(global[object] || window[object]);
                clearInterval(interval);
              } else {
                attempts += 1;
                if (attempts > 1000) {
                  clearInterval(interval);
                  reject(new Error(`Script load timed out: ${link}`));
                }
              }
            }, time);
          } else {
            resolve(global[object] || window[object]);
          }
        }))
      : Promise.resolve(global[object] || window[object])
  );
};


const buildFbLocale = locale => ((locale === language.default) ? `${locale}_IL` : `${locale}_${locale.toUpperCase()}`);
const buildFbSdkLink = (locale) => `https://connect.facebook.net/${buildFbLocale(locale)}/sdk.js#xfbml=1&version=v2.12`;

// TODO: Try to synchronise the load of the google maps SDK (module exports)
export const googleSDKLoader = locale => persistentScriptLoader(googleSdkLink + locale, 'google', 5, 'google.maps');
export const googleReCaptchaLoader = locale => persistentScriptLoader(googleReCaptchaConfig.url + locale, 'grecaptcha');
export const loadMarkerClusterer = () => persistentScriptLoader(config.clustererLibUrl, 'MarkerClusterer');
export const googleRemarketingLoader = () => persistentScriptLoader(remarketing.url, 'google_trackConversion');
export const facebookSDKLoader = (locale) => scriptLoader(buildFbSdkLink(locale), 'facebook-jssdk');
export const googleAuthSDKLoader = (locale) => scriptLoader(`${config.authLibUrl}?language=${locale}`);
export const googleTagLoader = () => scriptLoader(googleTagLink);
export const turnstileLoader = () => persistentScriptLoader(turnstileLink, 'turnstile');
